/**
 * Darabara commerce diagnostics for Microsoft Clarity.
 *
 * Privacy contract:
 * - Sends only fixed event names and coarse state labels.
 * - Never sends customer data, discount-code text, product data, cart tokens,
 *   URLs, or exact monetary values.
 * - Reads the same rendered free-shipping threshold as the cart progress bar.
 */

const TRACKER_NAME = 'DarabaraClarityCommerceTracking';

if (!window[TRACKER_NAME]) {
  const EVENTS = Object.freeze({
    cartOpened: 'db_cart_opened',
    addConfirmed: 'db_add_to_cart_confirmed',
    cartUpdated: 'db_cart_updated',
    discountApplied: 'db_discount_applied',
    discountRemoved: 'db_discount_removed',
    shippingLostAfterDiscount: 'db_shipping_lost_after_discount',
    checkoutStarted: 'db_checkout_started',
    expressCheckoutStarted: 'db_express_checkout_started',
  });

  const TAGS = Object.freeze({
    cartBand: 'db_cart_band_seen',
    cartAction: 'db_cart_action_seen',
    discountState: 'db_discount_state_seen',
    freeShippingState: 'db_free_shipping_state_seen',
    shippingOutcome: 'db_shipping_outcome_seen',
  });

  const state = {
    current: null,
    evaluationInFlight: false,
    evaluationTimer: null,
    pendingEvents: new Set(),
    pendingActions: new Set(),
    sentTags: new Set(),
    lastCartOpenAt: 0,
    lastCheckoutAt: 0,
    lastExpressCheckoutAt: 0,
  };

  /** Queue calls until the Clarity tracking script is ready. */
  window.clarity =
    window.clarity ||
    function () {
      window.clarity.q = window.clarity.q || [];
      window.clarity.q.push(arguments);
    };

  /** @param {string} eventName */
  function emitEvent(eventName) {
    window.clarity('event', eventName);
  }

  /**
   * Tag values are deliberately fixed labels. Repeated values are suppressed
   * because Clarity keeps every value seen for a tag during the session.
   * @param {string} key
   * @param {string} value
   */
  function setTag(key, value) {
    const signature = `${key}:${value}`;
    if (state.sentTags.has(signature)) return;

    state.sentTags.add(signature);
    window.clarity('set', key, value);
  }

  /** @returns {number | null} */
  function getShippingThresholdCents() {
    const root = document.querySelector('[data-shipping-bar]');
    if (!(root instanceof HTMLElement)) return null;

    const baseThreshold = Number.parseFloat(root.dataset.threshold || '');
    const countryCode = root.dataset.countryCode;
    if (!Number.isFinite(baseThreshold)) return null;

    if (countryCode === 'CA' || countryCode === 'US') {
      return Math.round(baseThreshold * 100);
    }

    const rate = Number.parseFloat(window.Shopify?.currency?.rate || '1');
    return Math.ceil(baseThreshold * 100 * (Number.isFinite(rate) ? rate : 1));
  }

  /** @returns {Promise<Record<string, any>>} */
  async function fetchCart() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
    return response.json();
  }

  /**
   * Convert the cart into coarse, non-identifying labels before anything is
   * passed to Clarity. Exact amounts and code text never leave this function.
   * @param {Record<string, any>} cart
   */
  function classifyCart(cart) {
    const itemCount = Number(cart.item_count) || 0;
    const total = Number(cart.total_price) || 0;
    const originalTotal = Number(cart.original_total_price ?? cart.items_subtotal_price ?? total) || total;
    const fullyDiscountedGiftValue = Array.isArray(cart.items)
      ? cart.items.reduce((sum, item) => {
          const quantity = Number(item?.quantity) || 0;
          const originalLinePrice =
            Number(item?.original_line_price) || (Number(item?.original_price) || 0) * quantity;
          const finalLinePrice = Number(item?.final_line_price ?? item?.line_price ?? originalLinePrice) || 0;

          // A free-gift app represents the gift as a normal product with a
          // 100% line discount. Its catalogue value must not make a paid cart
          // appear to have qualified for shipping before a discount.
          return finalLinePrice === 0 && originalLinePrice > 0 ? sum + originalLinePrice : sum;
        }, 0)
      : 0;
    const paidOriginalTotal = Math.max(total, originalTotal - fullyDiscountedGiftValue);
    const totalDiscount = Number(cart.total_discount) || 0;
    const threshold = getShippingThresholdCents();

    const hasApplicableCode =
      Array.isArray(cart.discount_codes) && cart.discount_codes.some((discount) => discount?.applicable !== false);
    const hasCartDiscount =
      Array.isArray(cart.cart_level_discount_applications) && cart.cart_level_discount_applications.length > 0;
    const hasLineDiscount =
      Array.isArray(cart.items) &&
      cart.items.some(
        (item) => Array.isArray(item?.line_level_discount_allocations) && item.line_level_discount_allocations.length > 0,
      );
    const hasDiscount = totalDiscount > 0 || hasApplicableCode || hasCartDiscount || hasLineDiscount;

    if (itemCount === 0) {
      return {
        hasDiscount,
        cartBand: 'empty',
        freeShippingState: 'not_applicable',
        shippingOutcome: 'empty_cart',
      };
    }

    if (!threshold) {
      return {
        hasDiscount,
        cartBand: 'threshold_unknown',
        freeShippingState: 'unknown',
        shippingOutcome: 'threshold_unknown',
      };
    }

    if (total >= threshold) {
      return {
        hasDiscount,
        cartBand: 'free_shipping_qualified',
        freeShippingState: 'qualified',
        shippingOutcome: 'qualified_after_discount',
      };
    }

    if (hasDiscount && paidOriginalTotal >= threshold && total < threshold) {
      return {
        hasDiscount,
        cartBand: 'discount_threshold_conflict',
        freeShippingState: 'not_qualified',
        shippingOutcome: 'lost_after_discount',
      };
    }

    return {
      hasDiscount,
      cartBand: 'below_free_shipping_threshold',
      freeShippingState: 'not_qualified',
      shippingOutcome: hasDiscount ? 'below_threshold_with_discount' : 'below_threshold_before_discount',
    };
  }

  /** @param {ReturnType<typeof classifyCart>} current */
  function publishState(current) {
    const previous = state.current;

    // Non-identifying QA markers make the active classification auditable in
    // a theme preview without exposing cart contents or exact values.
    document.documentElement.dataset.dbClarityCartBand = current.cartBand;
    document.documentElement.dataset.dbClarityDiscountState = current.hasDiscount ? 'applied' : 'none';
    document.documentElement.dataset.dbClarityShippingOutcome = current.shippingOutcome;

    setTag(TAGS.cartBand, current.cartBand);
    setTag(TAGS.discountState, current.hasDiscount ? 'applied' : 'none');
    setTag(TAGS.freeShippingState, current.freeShippingState);
    setTag(TAGS.shippingOutcome, current.shippingOutcome);

    if (previous && !previous.hasDiscount && current.hasDiscount) {
      emitEvent(EVENTS.discountApplied);
      setTag(TAGS.cartAction, 'discount_applied');
    }

    if (previous && previous.hasDiscount && !current.hasDiscount) {
      emitEvent(EVENTS.discountRemoved);
      setTag(TAGS.cartAction, 'discount_removed');
    }

    if (
      current.shippingOutcome === 'lost_after_discount' &&
      previous?.shippingOutcome !== 'lost_after_discount'
    ) {
      emitEvent(EVENTS.shippingLostAfterDiscount);
    }

    state.current = current;
  }

  async function evaluateCart() {
    if (state.evaluationInFlight) return;
    state.evaluationInFlight = true;

    const queuedEvents = Array.from(state.pendingEvents);
    const queuedActions = Array.from(state.pendingActions);
    state.pendingEvents.clear();
    state.pendingActions.clear();

    try {
      const cart = await fetchCart();
      publishState(classifyCart(cart));
      queuedActions.forEach((action) => setTag(TAGS.cartAction, action));
      queuedEvents.forEach(emitEvent);
    } catch (error) {
      // Tracking must never interrupt shopping. Keep diagnostics to the local
      // console and do not forward error details to Clarity.
      console.debug('Clarity commerce diagnostics skipped:', error);
    } finally {
      state.evaluationInFlight = false;

      if (state.pendingEvents.size || state.pendingActions.size) {
        scheduleEvaluation();
      }
    }
  }

  /**
   * Coalesce events produced by the same cart render into one read-only cart
   * request, while preserving every fixed diagnostic event/action label.
   * @param {{ eventName?: string, action?: string }} [options]
   */
  function scheduleEvaluation({ eventName, action } = {}) {
    if (eventName) state.pendingEvents.add(eventName);
    if (action) state.pendingActions.add(action);

    window.clearTimeout(state.evaluationTimer);
    state.evaluationTimer = window.setTimeout(evaluateCart, 180);
  }

  /** @param {Event} event */
  function handleCartLinesUpdate(event) {
    const action = typeof event.action === 'string' ? event.action : 'update';
    const eventName = action === 'add' ? EVENTS.addConfirmed : EVENTS.cartUpdated;
    const result = event.promise;

    if (result && typeof result.then === 'function') {
      result
        .then(({ detail } = {}) => {
          if (detail?.didError) return;
          scheduleEvaluation({ eventName, action });
        })
        .catch(() => {});
      return;
    }

    scheduleEvaluation({ eventName, action });
  }

  /** @param {Event} event */
  function handleDiscountUpdate(event) {
    const result = event.promise;
    if (result && typeof result.then === 'function') {
      result.then(() => scheduleEvaluation()).catch(() => {});
      return;
    }

    scheduleEvaluation();
  }

  function recordCartOpen() {
    const now = Date.now();
    if (now - state.lastCartOpenAt < 750) return;
    state.lastCartOpenAt = now;
    scheduleEvaluation({ eventName: EVENTS.cartOpened, action: 'cart_opened' });
  }

  function recordCheckoutStart() {
    const now = Date.now();
    if (now - state.lastCheckoutAt < 2000) return;
    state.lastCheckoutAt = now;

    // Synchronous so the event is queued before checkout navigation begins.
    emitEvent(EVENTS.checkoutStarted);
    setTag(TAGS.cartAction, 'checkout_started');
    scheduleEvaluation();
  }

  function recordExpressCheckoutStart() {
    const now = Date.now();
    if (now - state.lastExpressCheckoutAt < 2000) return;
    state.lastExpressCheckoutAt = now;

    emitEvent(EVENTS.expressCheckoutStarted);
    setTag(TAGS.cartAction, 'express_checkout_started');
    scheduleEvaluation();
  }

  function init() {
    document.documentElement.dataset.dbClarityCommerce = 'ready';

    document.addEventListener('shopify:cart:lines-update', handleCartLinesUpdate);
    document.addEventListener('shopify:cart:discount-update', handleDiscountUpdate);
    document.addEventListener('shopify:cart:view', recordCartOpen);

    // Fallback for drawer restores where the standard cart-view event may have
    // fired before this module initialized. Ignore menu/search/chat drawers.
    document.addEventListener('theme-drawer:open', (event) => {
      if (event.target instanceof Element && event.target.querySelector('cart-drawer')) {
        recordCartOpen();
      }
    });

    document.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;

        if (event.target.closest('#checkout, [name="checkout"], .cart__checkout-button')) {
          recordCheckoutStart();
          return;
        }

        if (event.target.closest('shopify-accelerated-checkout-cart, .additional-checkout-buttons')) {
          recordExpressCheckoutStart();
        }
      },
      { capture: true },
    );

    document.addEventListener(
      'submit',
      (event) => {
        const submitter = event.submitter;
        if (
          submitter instanceof Element &&
          submitter.matches('#checkout, [name="checkout"], .cart__checkout-button')
        ) {
          recordCheckoutStart();
        }
      },
      { capture: true },
    );

    if (window.location.pathname === '/cart') {
      scheduleEvaluation({ eventName: EVENTS.cartOpened, action: 'cart_page_viewed' });
    }
  }

  window[TRACKER_NAME] = Object.freeze({
    refresh: () => scheduleEvaluation(),
    getState: () => (state.current ? { ...state.current } : null),
  });

  init();
}
